import flatten from 'lodash/flatten';
import { PresentationMutationAPI } from '../api/presentation-mutation-api';
import { PresentationProps } from '../props/presentation/presentation-props';
import { PresentationStore } from '../store/presentation-store';
import { getHashString, JSF32UniqueIDGenerator } from '../utils/id-generator';
import { IMAGE_MIME_TYPE, AUDIO_MIME_TYPE, VIDEO_MIME_TYPE } from '../constants/media-constants';
import { deepIterate } from '../utils/object-utils';
import { getExtnameFromUrl, getImageSizeFromUrl } from '../utils/url-utils';
import { DataModel } from './data-model';
import type { ModelTypesMap } from '../store/data-model-store';
import type { ICTBlip, ICTTableStyleList, ICTTextBulletSizePercent } from '@tencent/slide-sdk-pb-model';
import type { IEventManager } from '../manager/event-manager';
import type { INotesMasterDataModel } from './notes-master-data-model';
import type { INoteSlideDataModel } from './note-slide-data-model';
import type {
    IAudioResourceData,
    IFontResourceData,
    IImageResourceData,
    IResourceMapData,
    IVideoResourceData,
} from '../data/resource-map-data';
import type { DataType, IEmbeddedFontFamily, IPresentationData } from '../data';
import type { IPresentationProps } from '../props/presentation/presentation-props';
import type { RestrictedDataModel } from './data-model';
import type { ILayoutDataModel } from './layout-data-model';
import type { IMasterDataModel } from './master-data-model';
import type { ISectionDataModel } from './section-data-model';
import type { ISlideDataModel } from './slide-data-model';
import type { IThemeDataModel } from './theme-data-model';
import type { IBaseContext } from '../context/base-context';
import type { IDGenerator, IJSF32UniqueIDGenerator } from '../utils/id-generator';
import type { DeepReadonly } from 'ts-essentials';

const RESOURCE_KEY = ['embed', 'link'] as (string | number)[];
const PICTURE_REACT_KEY = ['b', 'l', 'r', 't'] as (string | number)[];
const RECOLOR_KEY = ['grayscl', 'biLevel'] as (string | number)[];
const TEXT_KEY = ['buSzPct'] as (string | number)[];

export class PresentationDataModel extends DataModel<IPresentationData, IPresentationProps> {
    public context: IBaseContext = {
        getPresentation: () => this,
        getTarget: () => this,
    };
    public store: PresentationStore;
    public api: PresentationMutationAPI;
    public props: IPresentationProps;

    public parent: PresentationDataModel = this;
    private idGenerator: IJSF32UniqueIDGenerator;
    private populated = false;

    public constructor(data: IPresentationData, events: IEventManager) {
        super(events);
        this.store = new PresentationStore(data, events, this);
        this.api = new PresentationMutationAPI(this, events);
        this.props = new PresentationProps(this.store.data, this.context);
        this.idGenerator = new JSF32UniqueIDGenerator();
        this.idGenerator.isIdLegal = (newID) => !this.store.models.get(newID);
    }

    public getIDGenerator(): IDGenerator<string> {
        return this.idGenerator;
    }

    public get slideList(): ISlideDataModel[] {
        return this.store.slideList.toArray();
    }

    public get layoutList(): ILayoutDataModel[] {
        return flatten(this.masterList.map((master) => master.layoutList));
    }

    public get layoutMap(): Map<string, ILayoutDataModel> {
        return this.store.layoutMap.toMap();
    }

    public get masterList(): IMasterDataModel[] {
        return this.store.masterList.toArray();
    }

    public get themeList(): IThemeDataModel[] {
        return this.store.themeList.toArray();
    }

    public get sectionList(): ISectionDataModel[] {
        return this.store.sectionList;
    }

    public get notesMasterMap(): Map<string, INotesMasterDataModel> {
        return this.store.notesMasterMap.toMap();
    }

    public get noteSlideMap(): Map<string, INoteSlideDataModel> {
        return this.store.noteSlideMap.toMap();
    }

    public get tableStyleList(): DeepReadonly<ICTTableStyleList> | undefined {
        return this.store.data.tblStyleList;
    }

    public getEvents() {
        return this.events;
    }

    public getModel<Type extends DataType>(modelId: string, ...dataType: Type[]): ModelTypesMap[Type] | undefined {
        return this.store.models.get(modelId, ...dataType);
    }

    public getPageIndexById(id: string) {
        const page = this.getSlideById(id) || this.getLayoutById(id) || this.getMasterById(id);
        const slideIndex = this.slideList.indexOf(page as ISlideDataModel);
        const layoutIndex = this.layoutList.indexOf(page as ILayoutDataModel);
        const masterIndex = this.masterList.indexOf(page as IMasterDataModel);
        if (slideIndex > -1) return slideIndex;
        if (layoutIndex > -1) return layoutIndex;
        if (masterIndex > -1) return masterIndex;
        return -1;
    }

    public getSlideById(id: string): ISlideDataModel | undefined {
        return this.store.getSlideById(id);
    }

    public getLayoutById(id: string): ILayoutDataModel | undefined {
        return this.store.getLayoutById(id);
    }

    public getMasterById(id: string): IMasterDataModel | undefined {
        return this.store.getMasterById(id);
    }

    public getThemeById(id: string): IThemeDataModel | undefined {
        return this.store.getThemeById(id);
    }

    public getImageResourceById(id: string): IImageResourceData | undefined {
        return this.props.resourceMap.images[id];
    }

    public getVideoResourceById(id: string): IVideoResourceData | undefined {
        return this.props.resourceMap.videos[id];
    }

    public getAudioResourceById(id: string): IAudioResourceData | undefined {
        return this.props.resourceMap.audios[id];
    }

    public getFontResourceById(id: string): IFontResourceData | undefined {
        return this.props.resourceMap.fonts[id];
    }

    public hasResource(id: string) {
        const { images, videos, audios, fonts } = this.props.resourceMap;
        return !!(images[id] || videos[id] || audios[id] || fonts[id]);
    }

    /**
     * 1. 可能出现极小值，会导致 pb decode 失败
     * 2. 重新着色数据可能会出现错误的 grayscl 或者 biLevel
     * @param data
     */
    public modifyData<T extends object>(data: T) {
        let isModified = false;
        // eslint-disable-next-line complexity
        deepIterate(data, (val, key, data) => {
            if (PICTURE_REACT_KEY.includes(key) && typeof val === 'number' && val.toString().includes('.')) {
                data[key] = Number(Number(val).toFixed(0)) as typeof val;
                isModified = true;
            } else if (RECOLOR_KEY.includes(key)) {
                const removeGrayscl =
                    key === 'grayscl' &&
                    (val as ICTBlip['grayscl'])?.length === 1 &&
                    (val as ICTBlip['grayscl'])?.[0] === false;
                const removeBiLevel =
                    key === 'biLevel' &&
                    (val as ICTBlip['biLevel'])?.length === 1 &&
                    // 数据中可能有错误的0或者false
                    ((val as ICTBlip['biLevel'])?.[0].thresh === 0 || (val as any)?.[0].thresh === false);
                if (removeGrayscl || removeBiLevel) {
                    Reflect.deleteProperty(data, key);
                    isModified = true;
                }
            } else if (TEXT_KEY.includes(key)) {
                const pctObj = data[key] as ICTTextBulletSizePercent | undefined;
                if (pctObj?.val) {
                    // 如果 buSzPct 是小数，需要取整
                    const pct = Number(Number(pctObj.val).toFixed(0));
                    // Description:  The MinInclusive constraint failed. The value must be greater than or equal to 25000.
                    if (pct < 25000 || pct > 400000) {
                        pctObj.val = '100000';
                        isModified = true;
                    } else if (pctObj.val !== pct.toString()) {
                        pctObj.val = pct.toString();
                        isModified = true;
                    }
                }
            }
        });
        return isModified;
    }

    public findWildResourceMap<T extends object>(
        data: T,
        rewriteResourceId = false,
    ): IResourceMapData & { count: number } {
        const resourceMap: IResourceMapData & { count: number } = {
            images: {},
            videos: {},
            audios: {},
            fonts: {},
            count: 0,
        };

        // eslint-disable-next-line complexity
        deepIterate(data, (url, key, data) => {
            if (
                RESOURCE_KEY.includes(key) &&
                typeof url === 'string' &&
                (url.startsWith('https://') || url.startsWith('tdocs://') || url.startsWith('file://')) &&
                !this.hasResource(url)
            ) {
                const ext = getExtnameFromUrl(url);
                const id = rewriteResourceId ? `rel:${getHashString(url)}` : url;
                const audioMimeType = AUDIO_MIME_TYPE.get(ext);
                const videoMimeType = VIDEO_MIME_TYPE.get(ext);
                if (audioMimeType) {
                    resourceMap.audios[id] = {
                        id,
                        src: url,
                        type: audioMimeType,
                    };
                } else if (videoMimeType) {
                    resourceMap.videos[id] = {
                        id,
                        src: url,
                        type: videoMimeType,
                    };
                } else {
                    // 未识别到的 https 资源以图片 jpeg 格式处理
                    const imageMimeType = IMAGE_MIME_TYPE.get(ext) ?? 'image/jpeg';
                    const imageSize = getImageSizeFromUrl(url);
                    resourceMap.images[id] = {
                        id,
                        src: url,
                        type: imageMimeType,
                        ...imageSize,
                    };
                }
                resourceMap.count += 1;
                data[key] = id as typeof url;
            }
        });

        return resourceMap;
    }
    public findUsedResourceMap<T extends object>(data: T): IResourceMapData {
        const resourceMap: IResourceMapData = {
            images: {},
            videos: {},
            audios: {},
            fonts: {},
        };

        deepIterate(data, (id, key) => {
            if (RESOURCE_KEY.includes(key) && typeof id === 'string') {
                const imageResource = this.getImageResourceById(id);
                if (imageResource) {
                    resourceMap.images[id] = imageResource;
                }

                const videoResource = this.getVideoResourceById(id);
                if (videoResource) {
                    resourceMap.videos[id] = videoResource;
                }

                const audioResource = this.getAudioResourceById(id);
                if (audioResource) {
                    resourceMap.audios[id] = audioResource;
                }
            }
        });

        return resourceMap;
    }

    public getEmbeddedFontFamilies(): IEmbeddedFontFamily[] {
        return (
            this.props.embeddedFontList?.embeddedFont?.map(({ font, regular, bold, italic, boldItalic }) => ({
                typeface: font?.typeface ?? '',
                subfamilies: {
                    regular: this.getFontById(regular?.id),
                    bold: this.getFontById(bold?.id),
                    italic: this.getFontById(italic?.id),
                    boldItalic: this.getFontById(boldItalic?.id),
                },
            })) ?? []
        );
    }

    public forEachDescendant(callback: (child: RestrictedDataModel) => void = () => void 0) {
        this.slideList.forEach((slide) => {
            callback(slide);
            slide.forEachDescendant(callback);
        });
        this.masterList.forEach((master) => {
            callback(master);
            master.forEachDescendant(callback);
        });
        this.layoutList.forEach((layout) => {
            callback(layout);
            layout.forEachDescendant(callback);
        });
        this.themeList.forEach((theme) => {
            callback(theme);
        });
        this.sectionList.forEach((section) => {
            callback(section);
        });
        this.notesMasterMap.forEach((notesMaster) => {
            callback(notesMaster);
            notesMaster.forEachDescendant(callback);
        });
    }

    /**
     * 实例化所有子 DataModel
     */
    public populate() {
        if (!this.populated) {
            this.populated = true;
            this.forEachDescendant();
        }
    }

    private getFontById(id = ''): { src: string } | undefined {
        const src = this.getFontResourceById(id)?.src ?? id;
        if (!src) {
            return;
        }

        return { src };
    }
}

export { IPresentationProps };
export type IPresentationDataModel = RestrictedDataModel<PresentationDataModel>;
